// COCOA sends a flat videoHistory on non-track responses and one keyed by track
// day id on track-enabled ones.

type WithVideoHistory =
  { userData?: { videoHistory?: CocoApi.EntityVideoHistory | null } | null } | null | undefined

/** Fields only ever present on a flat record, never on the day-keyed wrapper. */
const FLAT_HISTORY_KEYS = [
  'completed',
  'percentageViewed',
  'secondsViewed',
  'state',
  'timecode',
  'resumable',
  'duration',
] as const

const isDayKeyed = (history: CocoApi.EntityVideoHistory): history is CocoApi.VideoHistoryByDay =>
  !FLAT_HISTORY_KEYS.some((key) => key in history)

const markHistoryComplete = (existing?: CocoApi.VideoHistory): CocoApi.VideoHistory => ({
  ...existing,
  completed: true,
})

const getExistingDayHistory = (
  existing: CocoApi.EntityVideoHistory | null | undefined,
  dayId: string
): CocoApi.VideoHistory | undefined => {
  if (!existing) return undefined
  if (isDayKeyed(existing)) return existing[dayId]
  return existing
}

export const buildCompletedEntityVideoHistory = (
  existing: CocoApi.EntityVideoHistory | null | undefined,
  dayId?: string
): CocoApi.EntityVideoHistory => {
  if (dayId) {
    const dayKeyed: CocoApi.VideoHistoryByDay =
      existing && isDayKeyed(existing) ? { ...existing } : {}
    const existingDayHistory = getExistingDayHistory(existing, dayId)

    dayKeyed[dayId] = markHistoryComplete(existingDayHistory)
    return dayKeyed
  }

  // A flat record reads as complete for every day; leave it for the refetch.
  if (existing && isDayKeyed(existing)) return existing

  return markHistoryComplete(existing ?? undefined)
}

export const clearEntityVideoHistory = (
  existing: CocoApi.EntityVideoHistory | null | undefined,
  dayIds?: string[]
): CocoApi.EntityVideoHistory | null => {
  if (!existing || !dayIds?.length || !isDayKeyed(existing)) return null

  const remaining: CocoApi.VideoHistoryByDay = {}
  for (const [dayId, history] of Object.entries(existing)) {
    if (!dayIds.includes(dayId)) remaining[dayId] = history
  }

  return Object.keys(remaining).length ? remaining : null
}

// Day-keyed history without a dayId has no single correct occurrence to report.
export const getEntityVideoHistory = (
  entity: WithVideoHistory,
  dayId?: string
): CocoApi.VideoHistory | null => {
  const history = entity?.userData?.videoHistory
  if (!history) return null

  if (!isDayKeyed(history)) return history
  if (!dayId) return null

  return history[dayId] ?? null
}

export const getVideoProgress = (entity: WithVideoHistory, dayId?: string): number =>
  parseFloat(getEntityVideoHistory(entity, dayId)?.percentageViewed ?? '0')

export const isVideoCompleted = (entity: WithVideoHistory, dayId?: string): boolean => {
  const history = getEntityVideoHistory(entity, dayId)
  if (!history) return false

  return history.completed === true || parseFloat(history.percentageViewed ?? '0') >= 100
}
